import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send, Mic, Sparkles, Brain, TrendingUp, Lightbulb, X } from 'lucide-react';
import { saveChatMessage, fetchChatMessages } from '../shared/api/chat';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles, avatarStyles } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';

const JourneyChatPage = ({ 
  journeyData,
  userId, 
  sessionId,
  onComplete,
  onCancel
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const [aiMode, setAiMode] = useState(true);
  const [suggestedTechniques, setSuggestedTechniques] = useState([]);
  const [showExitModal, setShowExitModal] = useState(false);
  const [conversationInsights, setConversationInsights] = useState({
    messageCount: 0,
    keyThemes: [],
    emotionalTone: null,
    emotionalCues: []
  });
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  // ✅ Get journey-specific styling
  const journeyStyle = getJourneyConfig(journeyData?.category || journeyData?.title);

  const manifestationTechniques = {
    "369": {
      name: "369 Method",
      icon: "✨",
      description: "Write your desire 3 times in morning, 6 times afternoon, 9 times before bed",
      bestFor: ["abundance", "specific goals", "clarity"],
      trigger: ["want", "goal", "manifest", "money", "success"]
    },
    "scripting": {
      name: "Scripting",
      icon: "📝",
      description: "Write in present tense as if your desire has already manifested",
      bestFor: ["love", "lifestyle", "general manifestation"],
      trigger: ["relationship", "life", "future", "dream", "imagine"]
    },
    "watermethod": {
      name: "Water Method",
      icon: "💧",
      description: "Write affirmation on glass of water, drink it mindfully with intention",
      bestFor: ["healing", "quick shifts", "gratitude"],
      trigger: ["heal", "release", "let go", "cleanse", "grateful"]
    },
    "pillow": {
      name: "Pillow Method",
      icon: "🛏️",
      description: "Write desire on paper, place under pillow before sleep for subconscious programming",
      bestFor: ["love", "peace", "dreams"],
      trigger: ["sleep", "dream", "night", "rest", "peace"]
    },
    "55x5": {
      name: "55x5 Method",
      icon: "5️⃣",
      description: "Write your affirmation 55 times for 5 consecutive days",
      bestFor: ["focus", "persistence", "big goals"],
      trigger: ["focus", "discipline", "commit", "serious", "determined"]
    },
    "visualization": {
      name: "Visualization Anchoring",
      icon: "🎯",
      description: "Create a detailed mental movie of your desire, anchor it with a physical gesture",
      bestFor: ["confidence", "performance", "achievement"],
      trigger: ["see", "imagine", "picture", "visualize", "confident"]
    },
    "emotional_ladder": {
      name: "Emotional Ladder",
      icon: "🪜",
      description: "Move up emotional scale gradually - don't jump from fear to joy, climb step by step",
      bestFor: ["healing", "depression", "anxiety"],
      trigger: ["sad", "depressed", "anxious", "scared", "worried", "stuck", "low"]
    },
    "two_cup": {
      name: "Two Cup Method",
      icon: "🥤",
      description: "Label one cup 'current reality', one 'desired reality'. Pour water from first to second, drink",
      bestFor: ["transformation", "career", "life changes"],
      trigger: ["change", "shift", "transform", "career", "move"]
    },
    "gratitude_rampage": {
      name: "Gratitude Rampage",
      icon: "🙏",
      description: "Spend 5 minutes listing everything you're grateful for as fast as you can",
      bestFor: ["gratitude", "raising vibration", "quick mood shift"],
      trigger: ["grateful", "thankful", "appreciate", "blessed", "good"]
    }
  };

  useEffect(() => {
    initializeChat();
    initializeSpeechRecognition();
  }, []);

  useEffect(() => {
    scrollToBottom();
    analyzeConversation();
  }, [messages]);

  const handleExitClick = () => {
    setShowExitModal(true);
  };

  const handleConfirmExit = () => {
    sessionStorage.removeItem('selectedJourneyId');
    sessionStorage.removeItem('resumeSessionId');
    sessionStorage.removeItem('resumeStep');
    
    if (onCancel) {
      onCancel();
    }
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  const detectEmotionalCues = (text) => {
    const lower = text.toLowerCase();
    const cues = [];

    if (/(sad|depressed|down|hopeless|defeated)/.test(lower)) {
      cues.push({ emotion: 'sadness', intensity: 'high', technique: 'emotional_ladder' });
    }
    if (/(anxious|worried|scared|fear|afraid|nervous)/.test(lower)) {
      cues.push({ emotion: 'anxiety', intensity: 'high', technique: 'watermethod' });
    }
    if (/(angry|frustrated|mad|irritated)/.test(lower)) {
      cues.push({ emotion: 'anger', intensity: 'medium', technique: 'emotional_ladder' });
    }
    if (/(stuck|lost|confused|unclear)/.test(lower)) {
      cues.push({ emotion: 'confusion', intensity: 'medium', technique: '369' });
    }
    if (/(excited|happy|grateful|blessed|amazing)/.test(lower)) {
      cues.push({ emotion: 'joy', intensity: 'high', technique: 'gratitude_rampage' });
    }
    if (/(hopeful|optimistic|ready|motivated)/.test(lower)) {
      cues.push({ emotion: 'hope', intensity: 'medium', technique: 'scripting' });
    }
    if (/(want|need|desire|goal|manifest|attract)/.test(lower)) {
      cues.push({ emotion: 'desire', intensity: 'high', technique: '369' });
    }

    return cues;
  };

  const suggestTechniques = (userMessage, emotionalCues) => {
    const lower = userMessage.toLowerCase();
    const suggested = [];

    Object.entries(manifestationTechniques).forEach(([key, technique]) => {
      const matchScore = technique.trigger.filter(trigger => 
        lower.includes(trigger)
      ).length;

      if (matchScore > 0) {
        suggested.push({ ...technique, key, matchScore });
      }
    });

    emotionalCues.forEach(cue => {
      const technique = manifestationTechniques[cue.technique];
      if (technique && !suggested.find(s => s.key === cue.technique)) {
        suggested.push({ ...technique, key: cue.technique, matchScore: 10 });
      }
    });

    suggested.sort((a, b) => b.matchScore - a.matchScore);
    return suggested.slice(0, 2);
  };

  const analyzeConversation = () => {
    const userMessages = messages.filter(m => m.role === 'user');
    
    const themes = new Set();
    const allEmotionalCues = [];
    
    const emotionalWords = {
      positive: ['happy', 'excited', 'grateful', 'love', 'amazing', 'wonderful', 'joy', 'blessed'],
      negative: ['sad', 'angry', 'frustrated', 'scared', 'worried', 'anxious', 'fear', 'depressed'],
      neutral: ['think', 'feel', 'want', 'need', 'trying', 'working']
    };
    
    let positiveCount = 0;
    let negativeCount = 0;
    
    userMessages.forEach(msg => {
      const lower = msg.content.toLowerCase();
      
      const cues = detectEmotionalCues(msg.content);
      allEmotionalCues.push(...cues);
      
      emotionalWords.positive.forEach(word => {
        if (lower.includes(word)) positiveCount++;
      });
      emotionalWords.negative.forEach(word => {
        if (lower.includes(word)) negativeCount++;
      });
      
      if (/(love|relationship|partner|romance)/.test(lower)) themes.add('Love');
      if (/(money|wealth|abundance|financial)/.test(lower)) themes.add('Abundance');
      if (/(career|job|work|professional)/.test(lower)) themes.add('Career');
      if (/(health|body|wellness|healing)/.test(lower)) themes.add('Health');
      if (/(peace|calm|anxiety|stress)/.test(lower)) themes.add('Peace');
      if (/(success|achievement|goal|dream)/.test(lower)) themes.add('Success');
    });
    
    let emotionalTone = 'neutral';
    if (positiveCount > negativeCount * 1.5) emotionalTone = 'positive';
    else if (negativeCount > positiveCount * 1.5) emotionalTone = 'challenging';
    
    setConversationInsights({
      messageCount: userMessages.length,
      keyThemes: Array.from(themes).slice(0, 3),
      emotionalTone,
      emotionalCues: allEmotionalCues.slice(0, 3)
    });
  };

  const initializeChat = async () => {
    try {
      const { messages: history, error } = await fetchChatMessages(sessionId);
      
      if (error) {
        console.error('Error loading chat history:', error);
        sendWelcomeMessage();
        return;
      }
      
      if (history && history.length > 0) {
        setMessages(history);
      } else {
        sendWelcomeMessage();
      }
    } catch (err) {
      console.error('Chat initialization error:', err);
      sendWelcomeMessage();
    }
  };

  const sendWelcomeMessage = async () => {
    const welcomeContent = `Welcome to your ${journeyData.title} journey! 💝\n\nI'm here to help you manifest love and deepen connections. Let's explore what love means to you.\n\nWhat kind of love are you calling into your life?`;
    
    const welcomeMessage = {
      role: 'assistant',
      content: welcomeContent,
      created_at: new Date().toISOString()
    };

    setMessages([welcomeMessage]);

    await saveChatMessage({
      session_id: sessionId,
      user_id: userId,
      role: 'assistant',
      content: welcomeContent
    });
  };

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError('Voice input error. Please try again.');
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      setError('Voice input is not supported in your browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setError(null);
      } catch (err) {
        console.error('Voice recognition error:', err);
        setError('Could not start voice input.');
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateAIResponse = async (userMessage, conversationHistory) => {
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          max_tokens: 1024,
          messages: [
            {
              role: 'system',
              content: `You are an empathetic manifestation coach specializing in ${journeyData.title}. Your role is to:

1. Listen deeply and validate emotions
2. Ask powerful questions that create insights
3. Guide users to clarity about their desires
4. Help them identify limiting beliefs
5. Encourage emotional awareness and processing
6. Suggest practical manifestation techniques when appropriate
7. Keep responses warm, conversational, and under 150 words
8. Use emotional intelligence to meet users where they are

Be supportive, non-judgmental, and focus on empowering the user. Don't just give advice - help them discover their own answers through thoughtful questions.`
            },
            ...conversationHistory
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'API request failed');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('AI Error:', error);
      throw error;
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    const newUserMessage = {
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, newUserMessage]);

    await saveChatMessage({
      session_id: sessionId,
      user_id: userId,
      role: 'user',
      content: userMessage
    });

    const emotionalCues = detectEmotionalCues(userMessage);
    const techniques = suggestTechniques(userMessage, emotionalCues);
    setSuggestedTechniques(techniques);

    try {
      const conversationHistory = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          role: m.role,
          content: m.content
        }));

      conversationHistory.push({ role: 'user', content: userMessage });

      const aiResponse = await generateAIResponse(userMessage, conversationHistory);

      const assistantMessage = {
        role: 'assistant',
        content: aiResponse,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      await saveChatMessage({
        session_id: sessionId,
        user_id: userId,
        role: 'assistant',
        content: aiResponse
      });

      setIsLoading(false);
    } catch (err) {
      console.error('Message send error:', err);
      setError('Failed to get AI response. Please try again.');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-32" style={{ backgroundColor: "#F7F4F0" }}>
      {/* Exit Modal - Using theme styles */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.default.background} border-2 ${journeyStyle.borderColor} rounded-3xl p-6 max-w-md w-full shadow-2xl`}>
            <h3 className="text-gray-900 text-xl font-bold mb-3">Exit Session?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to exit? Your progress will be saved, but you'll need to start a new conversation next time.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelExit}
                className={`flex-1 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} py-3 px-6`}
              >
                Stay
              </button>
              <button
                onClick={handleConfirmExit}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - Using journey-specific styling */}
      <div className="p-4 flex-shrink-0" style={{ background: "rgba(247,244,240,0.95)", borderBottom: "1px solid #EDE7DC", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
        <div className="flex items-center justify-between">
          <button onClick={handleExitClick} className="transition-colors" style={{ color: "#9C9080" }}>
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#A8856A,#8B6A50)", boxShadow: "0 4px 12px rgba(168,133,106,0.35)" }}>
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: "#2B2420" }}>{journeyData.title}</h2>
              <p className="text-xs" style={{ color: "#9C9080" }}>
                AI Coach • {conversationInsights.messageCount} messages
                {conversationInsights.emotionalCues.length > 0 && ` • ${conversationInsights.emotionalCues[0].emotion}`}
              </p>
            </div>
          </div>
          
          <button 
            onClick={handleExitClick}
            className="p-2 rounded-full transition-all hover:text-red-600 hover:bg-red-50" style={{ color: "#9C9080" }}
            title="Exit session"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Key Themes */}
        {conversationInsights.keyThemes.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <TrendingUp className="w-4 h-4" style={{ color: "#A8856A" }} />
            {conversationInsights.keyThemes.map(theme => (
              <span 
                key={theme}
                className="px-3 py-1 bg-white/80 backdrop-blur-sm rounded-full text-xs font-medium border border-[#EDE7DC] text-[#52392B]"
              >
                {theme}
              </span>
            ))}
          </div>
        )}

        {/* Suggested Techniques */}
        {suggestedTechniques.length > 0 && (
          <div className="mt-3 rounded-xl p-3" style={{ background: "#FAF8F5", border: "1px solid #EDE7DC" }}>
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#A8856A" }} />
              <div>
                <p className="text-sm font-semibold mb-2" style={{ color: "#52392B" }}>Suggested Manifestation Techniques:</p>
                <div className="space-y-2">
                  {suggestedTechniques.map((tech) => (
                    <div key={tech.key} className="text-xs" style={{ color: "#6B5040" }}>
                      <span className="font-bold">{tech.icon} {tech.name}:</span> {tech.description}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-2 text-center text-sm" style={{ background: "#F4EFE8", borderTop: "1px solid #EDE7DC", borderBottom: "1px solid #EDE7DC", color: "#52392B" }}>
          {error}
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-6 pb-32 space-y-6">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
          >
            {message.role === 'assistant' && (
              <div className="flex items-start gap-3 max-w-[85%]">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#A8856A,#8B6A50)", boxShadow: "0 2px 8px rgba(168,133,106,0.3)" }}>
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div className="rounded-2xl rounded-tl-sm p-4" style={{ background: "#FFFFFF", border: "1px solid #EDE7DC", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#A8856A" }}>
                    <Sparkles className="w-3 h-3" />
                    AI Coach
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "#2B2420" }}>
                    {message.content}
                  </p>
                </div>
              </div>
            )}

            {message.role === 'user' && (
              <div className="rounded-2xl rounded-tr-sm p-4 max-w-[85%]" style={{ background: "linear-gradient(135deg,#A8856A,#8B6A50)", boxShadow: "0 4px 16px rgba(168,133,106,0.25)" }}>
                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
              </div>
            )}
          </div>
        ))}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-start gap-3 animate-fade-in">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#A8856A,#8B6A50)", boxShadow: "0 2px 8px rgba(168,133,106,0.3)" }}>
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="rounded-2xl rounded-tl-sm p-4" style={{ background: "#FFFFFF", border: "1px solid #EDE7DC", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "#C4B9A8", animationDelay: "0ms" }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "#C4B9A8", animationDelay: "150ms" }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: "#C4B9A8", animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Section - Using theme styles */}
      <div className="fixed bottom-20 left-0 right-0 p-4 z-50" style={{ background: "#FFFFFF", borderTop: "1px solid #EDE7DC", boxShadow: "0 -4px 20px rgba(0,0,0,0.06)" }}>
        <div className="max-w-4xl mx-auto">
          {conversationInsights.messageCount >= 6 && (
            <button
              onClick={onComplete}
              className={`w-full ${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-3 px-6 mb-3 flex items-center justify-center gap-2`}
            >
              <Sparkles className="w-5 h-5" />
              {suggestedTechniques.length > 0 
                ? `Try ${suggestedTechniques[0].name} ${suggestedTechniques[0].icon}` 
                : 'Continue Your Journey'}
            </button>
          )}
          
          <div className="flex items-center gap-2 rounded-full p-2" style={{ background: "#F9F7F3", border: "1px solid #EDE7DC" }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Share what's on your mind..."
              disabled={isLoading}
              className="flex-1 bg-transparent text-gray-900 placeholder-gray-500 px-4 py-2.5 focus:outline-none disabled:opacity-50"
            />
            
            <button
              onClick={toggleVoiceInput}
              className={`p-3 rounded-full transition-all ${
                isListening 
                  ? 'bg-red-500 animate-pulse shadow-lg' 
                  : 'bg-[#F4F0EA] hover:bg-[#EDE7DC]'
              }`}
              disabled={isLoading}
              title="Voice input"
            >
              <Mic className="w-5 h-5" style={{ color: isListening ? "#FFFFFF" : "#A8856A" }} />
            </button>
            
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 shadow-lg`}
              title="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-2 text-center">
            <p className="text-xs flex items-center justify-center gap-2" style={{ color: "#9C9080" }}>
              <Sparkles className="w-3 h-3" />
              AI-powered manifestation coaching with emotional intelligence
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default JourneyChatPage;